/*
 * @Author: Ryan
 * @Date: 2026-02-03
 * @Description: Showcase section - Remove Video Text and Subtitles for Cleaner Footage
 */
import Image from 'next/image';
import React from 'react';

import { scrollToTop } from '@/utils/landing/scrollToTop';

interface ShowcaseItem {
  id: string;
  imageUrl: string;
  alt: string;
}

const showcaseItems: ShowcaseItem[] = [
  {
    id: 'fashion-video',
    imageUrl:
      'https://assets.falcocut.ai/ai-removal/fashion-video-text-removal-before-after.webp',
    alt: 'Fashion video text removal before and after',
  },
  {
    id: 'perfume-label',
    imageUrl:
      'https://assets.falcocut.ai/ai-removal/perfume-label-remover-before-after.webp',
    alt: 'Perfume label removal before and after',
  },
  {
    id: 'beauty-video',
    imageUrl:
      'https://assets.falcocut.ai/ai-removal/beauty-video-branding-remover-before-after.webp',
    alt: 'Beauty video branding removal before and after',
  },
];

// 箭头图标
const ArrowRightIcon: React.FC = () => (
  <svg viewBox="64 64 896 896" fill="currentColor" width="1em" height="1em">
    <path d="M869 487.8L491.2 159.9c-2.9-2.5-6.6-3.9-10.5-3.9h-88.5c-7.4 0-10.8 9.2-5.2 14l350.2 304H152c-4.4 0-8 3.6-8 8v60c0 4.4 3.6 8 8 8h585.1L386.9 854c-5.6 4.9-2.2 14 5.2 14h91.5c1.9 0 3.8-.7 5.2-2L869 536.2a32.07 32.07 0 000-48.4z" />
  </svg>
);

const ShowcaseSection: React.FC = () => {
  return (
    <section className="w-full bg-white py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-4 pad:mb-5 leading-tight">
          Remove Video Text and Subtitles for Cleaner Footage
        </h2>

        {/* Description */}
        <p className="text-sm pad:text-base sm:text-base text-center text-gray-600 max-w-5xl mx-auto mb-10 pad:mb-14 sm:mb-16 leading-relaxed">
          Experience a simple, productive cleanup tool for your videos. FalcoCut keeps
          your visuals distraction-free and prepares every clip for easy reuse across any
          platform. You get cleaner footage, faster edits, and a smoother path to
          publishing polished content.
        </p>

        {/* Showcase Cards Grid */}
        <div className="grid grid-cols-1 pad:grid-cols-3 gap-4 pad:gap-6 mb-10 pad:mb-14">
          {showcaseItems.map((item) => (
            <div key={item.id} className="rounded-2xl overflow-hidden">
              <Image
                src={item.imageUrl}
                alt={item.alt}
                width={380}
                height={0}
                className="w-full h-auto"
                sizes="(max-width: 768px) 100vw, 33vw"
              />
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => scrollToTop()}
            className="inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-semibold text-white rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg"
            style={{
              background: 'linear-gradient(90deg, #4D2FFF 0%, #EF5CFF 100%)',
            }}
          >
            <span>Remove Text from Video</span>
            <ArrowRightIcon />
          </button>
        </div>
      </div>
    </section>
  );
};

export default ShowcaseSection;
